import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { PathsEnum } from 'pages/Paths'

import { authApi } from 'features/auth/authApi'
import { getUserPreference, settingsServiceApiPaths } from 'features/settings/settingsApi'
import { apiPaths } from '../configs/apiConfigs'
import { env } from '../configs/env'
import { sendLogs } from './logger'
import { StorageEnum } from './storage'
import { getTntIdFromUrl, getTntIdFromStorage } from './tenantUrlUtils'

const authMiddleware = async (config: InternalAxiosRequestConfig<any>) => {
  let token: null | string
  const currentUser = window.localStorage.getItem(StorageEnum.currentUser) || '{}'

  try {
    token = (await authApi.getAuthToken()) || ''
    if (config?.headers) {
      config.headers.Authorization = `Bearer ${token}`
      config.headers['currentUser'] = currentUser
      config.headers['refresh_token'] = window.localStorage.getItem('refreshToken') || ''

      // CRITICAL: Use tntId from URL, but send both tntId and tenantId (GUID) in headers
      // 1. Try to get tntId from URL
      const tntIdFromUrl = getTntIdFromUrl()
      const storedTntId = getTntIdFromStorage()

      // 2. Get tenantId (GUID) for headers - use stored tenantId if tntId matches
      let tntId: string | null = null

      if (tntIdFromUrl && tntIdFromUrl === storedTntId) {
        // URL tntId matches stored tntId, use stored values
        tntId = tntIdFromUrl
      } else if (!tntIdFromUrl) {
        // No tntId in URL, fall back to stored values
        tntId = storedTntId
      }
      // Note: If tntId in URL differs from stored, we can't resolve tenantId here
      // This should trigger a tenant switch which will update both values

      if (tntId) {
        config.headers[UserPreferencesKeysEnum.outboundAiPreferredTenant] = tntId
      }
    }
  } catch (error) {
    token = null
  }

  return config
}

const claimAuthMiddleware = async (config: InternalAxiosRequestConfig<any>) => {
  let token: null | string
  const currentUser = window.localStorage.getItem(StorageEnum.currentUser) || '{}'
  const currentUrl = window.location.href

  const tenantSelectionRestricted = [PathsEnum.logout, PathsEnum.down].some((path) => currentUrl.includes(path))

  try {
    token = (await authApi.getAuthToken()) || ''

    if (!token) return config
    if (config?.headers) {
      config.headers.Authorization = `Bearer ${token}`
      config.headers['currentUser'] = currentUser

      // CRITICAL: Use tntId from URL, but send both tntId and tenantId (GUID) in headers
      // 1. Try to get tntId from URL
      const tntIdFromUrl = getTntIdFromUrl()
      const storedTntId = getTntIdFromStorage()

      // 2. Get tenantId (GUID) for headers - use stored tenantId if tntId matches
      let tntId: string | null = null

      if (tntIdFromUrl && tntIdFromUrl === storedTntId) {
        // URL tntId matches stored tntId, use stored values
        tntId = tntIdFromUrl
      } else if (!tntIdFromUrl) {
        // No tntId in URL, fall back to stored values
        tntId = storedTntId
      }

      if (!tenantSelectionRestricted && tntId) {
        config.headers[UserPreferencesKeysEnum.outboundAiPreferredTenant] = tntId
      }

      if (!tenantSelectionRestricted && !tntId) {
        const url = settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.outboundAiPreferredTenant)
        const ten = await getUserPreference(url)
        config.headers[UserPreferencesKeysEnum.outboundAiPreferredTenant] = ten?.value
        sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredTenant, ten?.value)
      }
    }
  } catch (error) {
    token = null
  }

  return config
}

const responseMiddleware = <T>(res: T) => res
const responseErrorMiddleware = async (err: AxiosError) => {
  const status = err.response?.status || 500

  sendLogs({ message: err.response?.data })

  if (status === 401) {
    console.log('401 error', err.response?.data)
    //await authApi.signOut()
  }

  if (status >= 500) {
    if (!err.config?.url?.includes(apiPaths.health()))
      await axios.get(`${env.VITE_CLAIMS_SERVICE_URL}${apiPaths.health()}`).catch(() => {
        window.location.replace(PathsEnum.down)
      })
  }

  return Promise.reject(err)
}

export const httpTenantsService = axios.create({
  baseURL: env.VITE_TENANT_ROLE_USER_SERVICE_URL,
})
httpTenantsService.interceptors.request.use(authMiddleware)
httpTenantsService.interceptors.response.use(responseMiddleware, responseErrorMiddleware)

export const httpClaims = axios.create({
  baseURL: env.VITE_CLAIMS_SERVICE_URL,
})
httpClaims.interceptors.request.use(claimAuthMiddleware)
httpClaims.interceptors.response.use(responseMiddleware, responseErrorMiddleware)

export const httpCallService = axios.create({
  baseURL: env.VITE_CALLS_SERVICE_URL,
})
httpCallService.interceptors.request.use(claimAuthMiddleware)
httpCallService.interceptors.response.use(responseMiddleware, responseErrorMiddleware)

export const exportedForTesting = {
  authMiddleware,
  claimAuthMiddleware,
  responseErrorMiddleware,
  httpTenantsService,
}
